/**
 * The Play tab during a tournament — FR-TOUR-4, FR-ENTRY-12, AC-27.
 *
 * The rotation screen answers "who is on next?" because in an ordinary session
 * nobody knows until the queue says so. A tournament already knows: the draw
 * was made before anybody arrived. So this screen answers a different
 * question — *which of the two courts am I reporting?* — and answers it by
 * putting both on screen at once.
 *
 * That keeps the tap budget where AC-27 puts it, and in fact beats the
 * multi-court case:
 *
 *   tap 1 — the winning side's Won button, on the card for its court
 *   tap 2 — the losing score
 *
 * Two taps, not three, because choosing a court and choosing a winner are the
 * same tap when both courts are visible. No keyboard, no modes, nothing to
 * confirm. A fixture already played shows its score instead of its buttons,
 * with Undo beside it, so the screen never offers to log something twice.
 *
 * The round shown follows the tournament while nobody has said otherwise, and
 * stops following the moment somebody navigates by hand — being moved off the
 * round you are reading, because a result landed on another court, is how a
 * score gets entered against the wrong fixture.
 */

import { useEffect, useRef, useState } from "react";
import { api, type TournamentFixture, type TournamentView } from "../api.js";

export function TournamentPlay({
  tournament,
  onWon,
  onChanged,
  error,
}: {
  tournament: TournamentView;
  onWon: (fixture: TournamentFixture, side: "A" | "B") => void;
  onChanged: () => Promise<void>;
  error: string | null;
}): React.JSX.Element {
  const lastRound = tournament.roundCount;
  const current = tournament.currentRoundNo ?? lastRound;

  const [round, setRound] = useState(current);
  /**
   * Whether the screen is still following the tournament.
   *
   * Set false by the arrows and true again by "Back to the live round". A
   * plain `round === current` test would not do: after somebody navigates back
   * to a finished round it would re-follow the instant the live round moved,
   * yanking them off the card they were reading.
   */
  const following = useRef(true);
  useEffect(() => {
    if (following.current) setRound(current);
  }, [current]);

  const fixtures = tournament.fixtures
    .filter((f) => f.roundNo === round)
    .sort((a, b) => a.courtNumber - b.courtNumber);

  const playingNow = new Set(
    fixtures.flatMap((f) => [...f.sideA, ...f.sideB]).map((p) => p.code),
  );
  const resting = tournament.teams
    .flatMap((t) => t.members)
    .filter((m) => !playingNow.has(m.code));

  const go = (to: number): void => {
    following.current = false;
    setRound(Math.min(Math.max(to, 1), lastRound));
  };

  const done = tournament.playedCount;
  const isBreakRound = tournament.breakAfterRounds.includes(round);

  return (
    <>
      <div className="row space-between tour-head">
        <div className="eyebrow">
          {done} of {tournament.fixtureCount} games played
        </div>
        {tournament.currentRoundNo === null ? (
          <span className="provisional-flag">all rounds played</span>
        ) : null}
      </div>

      <div className="row roundnav">
        <button className="mini" disabled={round <= 1} onClick={() => go(round - 1)} aria-label="Previous round">
          ‹
        </button>
        <b className="roundnav-label">
          Round {round} <span className="muted">of {lastRound}</span>
        </b>
        <button
          className="mini"
          disabled={round >= lastRound}
          onClick={() => go(round + 1)}
          aria-label="Next round"
        >
          ›
        </button>
        {round !== current ? (
          <button
            className="chip"
            onClick={() => {
              following.current = true;
              setRound(current);
            }}
          >
            Back to round {current}
          </button>
        ) : null}
      </div>

      {error ? <p className="error">{error}</p> : null}

      {fixtures.length === 0 ? (
        <section className="matchup empty">
          <p>No fixtures in this round.</p>
        </section>
      ) : (
        fixtures.map((fixture) => (
          <FixtureCard
            key={fixture.id}
            fixture={fixture}
            targetScore={tournament.targetScore}
            capScore={tournament.capScore}
            teamNameOf={(code) => tournament.teams.find((t) => t.code === code)?.name ?? code}
            onWon={(side) => onWon(fixture, side)}
            onChanged={onChanged}
          />
        ))
      )}

      <p className="resting">
        {resting.length > 0 ? (
          <>
            Resting: <b>{resting.map((m) => m.displayName).join(", ")}</b>
          </>
        ) : (
          "Everybody is on court this round."
        )}
        {isBreakRound ? " · break after this round" : null}
      </p>

      <NextRound tournament={tournament} round={round} />
    </>
  );
}

/**
 * One court's fixture.
 *
 * Unplayed: two names and a Won button a side, which is the card the whole
 * product is measured on, arranged exactly as the session one is so that
 * neither has to be learned twice.
 *
 * Played: the score, and Undo. Deliberately not the Won buttons greyed out —
 * a disabled control still invites the tap, and the useful thing to offer
 * somebody looking at a wrong score is the way to remove it.
 */
function FixtureCard({
  fixture,
  targetScore,
  capScore,
  teamNameOf,
  onWon,
  onChanged,
}: {
  fixture: TournamentFixture;
  targetScore: number;
  capScore: number;
  teamNameOf: (code: string) => string;
  onWon: (side: "A" | "B") => void;
  onChanged: () => Promise<void>;
}): React.JSX.Element {
  const result = fixture.result;

  return (
    <section className={result ? "matchup fixture played" : "matchup fixture"}>
      <h3 className="card-head">
        Court {fixture.courtNumber}
        <span className="muted small">
          to {targetScore}, cap {capScore}
        </span>
      </h3>

      <FixtureSide
        label="A"
        teamName={teamNameOf(fixture.sideATeamCode)}
        players={fixture.sideA}
        score={result ? result.sideAScore : null}
        won={result?.winnerSide === "A"}
        onWon={() => onWon("A")}
      />

      <div className="versus">vs</div>

      <FixtureSide
        label="B"
        teamName={teamNameOf(fixture.sideBTeamCode)}
        players={fixture.sideB}
        score={result ? result.sideBScore : null}
        won={result?.winnerSide === "B"}
        onWon={() => onWon("B")}
      />

      {result ? (
        <div className="card-foot">
          <button
            className="chipbtn"
            onClick={() => {
              void api.undoGame(result.gameId).then(onChanged);
            }}
          >
            Undo this result
          </button>
        </div>
      ) : null}
    </section>
  );
}

function FixtureSide({
  label,
  teamName,
  players,
  score,
  won,
  onWon,
}: {
  label: string;
  teamName: string;
  players: Array<{ code: string; displayName: string }>;
  score: number | null;
  won: boolean;
  onWon: () => void;
}): React.JSX.Element {
  return (
    <div className="side">
      <div className="side-names">
        {players.map((p) => (
          // Not a button. In a session a name is tappable because tapping it
          // swaps somebody in; here the draw decides who plays, and a control
          // that looks tappable and is not is worse than plain text.
          <span key={p.code} className="name name-fixed">
            <b>{p.displayName}</b>
            <em>{p.code}</em>
          </span>
        ))}
      </div>

      {score === null ? (
        <button className="won" onClick={onWon} aria-label={`${teamName} side ${label} won`}>
          Won
        </button>
      ) : (
        <span className={won ? "fixture-score num win" : "fixture-score num"}>{score}</span>
      )}
    </div>
  );
}

/**
 * What is coming, for the two people about to be called on.
 *
 * Small, and at the bottom, because it is the only thing on this screen that
 * is not about the game in front of you — but people do ask, and the schedule
 * is the one thing a tournament has that a session does not.
 */
function NextRound({
  tournament,
  round,
}: {
  tournament: TournamentView;
  round: number;
}): React.JSX.Element | null {
  const next = tournament.fixtures.filter((f) => f.roundNo === round + 1);
  if (next.length === 0) return null;

  return (
    <section className="block">
      <h3 className="card-head">Next round</h3>
      {next
        .sort((a, b) => a.courtNumber - b.courtNumber)
        .map((f) => (
          <div key={f.id} className="rotrow">
            <span className="who">
              Court {f.courtNumber}: {f.sideA.map((p) => p.displayName).join(" & ")} vs{" "}
              {f.sideB.map((p) => p.displayName).join(" & ")}
            </span>
          </div>
        ))}
    </section>
  );
}
